/**
 * DischargePage.jsx — Clinical Discharge Workflow
 *
 * Steps:
 *   1. Confirm individual
 *   2. Discharge reason
 *   3. Discharge summary
 *   4. Aftercare plan
 *   5. Final billing reconciliation note
 *   6. Review & confirm
 *
 * Powered by Cold Harbor Digital
 */

import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";
import { getIndividual } from "../services/individualService";
import { updateAdmissionStatus } from "../services/intakeService";
import {
  ArrowLeft, ArrowRight, Check, User, AlertTriangle,
  FileText, ClipboardList, LogOut, Heart,
} from "lucide-react";

const DISCHARGE_REASONS = [
  { value: "treatment_complete", label: "Treatment Goals Met" },
  { value: "client_request", label: "Client Requested Discharge" },
  { value: "moved", label: "Moved Out of Service Area" },
  { value: "transferred", label: "Transferred to Another Provider" },
  { value: "lost_contact", label: "Lost Contact / Unable to Locate" },
  { value: "non_compliant", label: "Non-Compliance with Treatment" },
  { value: "incarceration", label: "Incarceration" },
  { value: "hospitalization", label: "Long-Term Hospitalization" },
  { value: "insurance_loss", label: "Loss of Insurance Coverage" },
  { value: "deceased", label: "Deceased" },
  { value: "administrative", label: "Administrative Discharge" },
  { value: "other", label: "Other" },
];

const S = {
  label: { display: "block", fontSize: 11, fontWeight: 700, color: "#a0aec0", marginBottom: 4, textTransform: "uppercase", letterSpacing: 0.5 },
  input: {
    width: "100%", padding: "10px 12px", borderRadius: 10, fontSize: 14, fontWeight: 500,
    background: "rgba(15,23,42,0.6)", color: "#e2e8f0",
    border: "1px solid rgba(99,179,237,0.1)", outline: "none", boxSizing: "border-box",
  },
  select: {
    width: "100%", padding: "10px 12px", borderRadius: 10, fontSize: 14, fontWeight: 500,
    background: "rgba(15,23,42,0.6)", color: "#e2e8f0",
    border: "1px solid rgba(99,179,237,0.1)", outline: "none", appearance: "auto", boxSizing: "border-box",
  },
  textarea: {
    width: "100%", padding: "10px 12px", borderRadius: 10, fontSize: 14, fontWeight: 500,
    background: "rgba(15,23,42,0.6)", color: "#e2e8f0",
    border: "1px solid rgba(99,179,237,0.1)", outline: "none",
    minHeight: 120, resize: "vertical", fontFamily: "inherit", boxSizing: "border-box",
  },
  card: {
    background: "rgba(15,23,42,0.7)", border: "1px solid rgba(99,179,237,0.08)",
    borderRadius: 16, padding: 16, marginBottom: 12,
  },
  fieldGroup: { marginBottom: 16 },
};

export default function DischargePage() {
  const navigate = useNavigate();
  const { individualId } = useParams();
  const { profile } = useAuth();

  const [individual, setIndividual] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [step, setStep] = useState(0); // 0=form, 1=review

  const [form, setForm] = useState({
    reason: "",
    reasonOther: "",
    summary: "",
    aftercarePlan: "",
    aftercareProviders: "",
    aftercareMedications: "",
    finalNotes: "",
    confirmDischarge: false,
  });

  useEffect(() => {
    if (!individualId) return;
    getIndividual(individualId).then(ind => {
      setIndividual(ind);
      setLoading(false);
    });
  }, [individualId]);

  const update = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const handleSubmit = async () => {
    if (!form.reason) { setError("Discharge reason is required."); return; }
    if (!form.summary.trim()) { setError("Discharge summary is required."); return; }
    if (!form.confirmDischarge) { setError("You must confirm the discharge."); return; }

    setSaving(true);
    setError("");

    const result = await updateAdmissionStatus(individualId, "discharged", {
      reason: form.reason === "other" ? form.reasonOther : form.reason,
      summary: form.summary,
      aftercarePlan: [form.aftercarePlan, form.aftercareProviders, form.aftercareMedications].filter(Boolean).join("\n\n"),
      finalNotes: form.finalNotes,
    });

    if (result.success) {
      navigate(`/individual/${individualId}`, { state: { discharged: true } });
    } else {
      setError(result.error || "Failed to discharge.");
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div style={{ minHeight: "100vh", background: "#0a0f1a", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <div style={{ width: 32, height: 32, borderRadius: "50%", border: "3px solid rgba(99,179,237,0.1)", borderTopColor: "#63b3ed", animation: "spin 1s linear infinite" }} />
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  if (!individual) {
    return (
      <div style={{ minHeight: "100vh", background: "#0a0f1a", color: "#e2e8f0", display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column", fontFamily: "'DM Sans', sans-serif" }}>
        <User size={40} style={{ color: "#2d3748", marginBottom: 12 }} />
        <div style={{ fontSize: 16, fontWeight: 800, color: "#4a5568" }}>Individual Not Found</div>
        <button onClick={() => navigate(-1)} style={{ marginTop: 16, padding: "10px 24px", borderRadius: 12, fontSize: 13, fontWeight: 700, background: "linear-gradient(135deg, #3182ce, #2b6cb0)", color: "white", border: "none", cursor: "pointer" }}>
          Go Back
        </button>
      </div>
    );
  }

  const ind = individual;
  const reasonLabel = DISCHARGE_REASONS.find(r => r.value === form.reason)?.label || form.reasonOther || "—";

  return (
    <div style={{ minHeight: "100vh", background: "#0a0f1a", color: "#e2e8f0", fontFamily: "'DM Sans', -apple-system, sans-serif" }}>

      {/* Header */}
      <header style={{
        padding: "16px 20px", background: "linear-gradient(135deg, #0f1729, #162033)",
        borderBottom: "1px solid rgba(252,129,129,0.15)", position: "sticky", top: 0, zIndex: 50,
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <button onClick={() => navigate(-1)} style={{ background: "none", border: "none", cursor: "pointer", color: "#a0aec0" }}>
            <ArrowLeft size={20} />
          </button>
          <div>
            <div style={{ fontSize: 16, fontWeight: 800, color: "#fc8181" }}>Discharge</div>
            <div style={{ fontSize: 11, color: "#718096" }}>
              {ind.firstName} {ind.lastName}
              {ind.dob && ` · DOB: ${ind.dob}`}
            </div>
          </div>
        </div>
      </header>

      <div style={{ padding: "16px 20px", paddingBottom: 120 }}>

        {/* Warning Banner */}
        <div style={{
          ...S.card, borderColor: "rgba(252,129,129,0.2)", background: "rgba(252,129,129,0.04)",
          display: "flex", gap: 10, alignItems: "flex-start",
        }}>
          <AlertTriangle size={18} style={{ color: "#fc8181", flexShrink: 0, marginTop: 2 }} />
          <div>
            <div style={{ fontSize: 13, fontWeight: 800, color: "#fc8181" }}>Discharge is a significant clinical action</div>
            <div style={{ fontSize: 11, color: "#a0aec0", lineHeight: 1.5, marginTop: 4 }}>
              This will change the individual's status to "Discharged" and remove them from active caseloads.
              All clinical records will be preserved. This action is logged in the audit trail.
            </div>
          </div>
        </div>

        {step === 0 && (
          <div>
            {/* Discharge Reason */}
            <div style={S.fieldGroup}>
              <label style={S.label}>Discharge Reason *</label>
              <select style={S.select} value={form.reason} onChange={e => update("reason", e.target.value)}>
                <option value="">Select reason...</option>
                {DISCHARGE_REASONS.map(r => (
                  <option key={r.value} value={r.value}>{r.label}</option>
                ))}
              </select>
            </div>

            {form.reason === "other" && (
              <div style={S.fieldGroup}>
                <label style={S.label}>Specify Reason</label>
                <input style={S.input} value={form.reasonOther} onChange={e => update("reasonOther", e.target.value)} placeholder="Describe reason..." />
              </div>
            )}

            {/* Discharge Summary */}
            <div style={S.fieldGroup}>
              <label style={S.label}>Discharge Summary *</label>
              <textarea style={S.textarea} value={form.summary} onChange={e => update("summary", e.target.value)}
                placeholder="Summarize the individual's course of treatment, progress toward goals, current functioning, and reason for discharge..." />
            </div>

            {/* Aftercare */}
            <div style={{ fontSize: 13, fontWeight: 800, color: "#4fd1c5", marginBottom: 12, display: "flex", alignItems: "center", gap: 8 }}>
              <Heart size={16} /> Aftercare Plan
            </div>

            <div style={S.fieldGroup}>
              <label style={S.label}>Aftercare Recommendations</label>
              <textarea style={{ ...S.textarea, minHeight: 80 }} value={form.aftercarePlan} onChange={e => update("aftercarePlan", e.target.value)}
                placeholder="Recommendations for continued care, follow-up appointments, support groups..." />
            </div>

            <div style={S.fieldGroup}>
              <label style={S.label}>Aftercare Providers / Referrals</label>
              <textarea style={{ ...S.textarea, minHeight: 60 }} value={form.aftercareProviders} onChange={e => update("aftercareProviders", e.target.value)}
                placeholder="Names and contact info for providers the individual is being referred to..." />
            </div>

            <div style={S.fieldGroup}>
              <label style={S.label}>Medications at Discharge</label>
              <textarea style={{ ...S.textarea, minHeight: 60 }} value={form.aftercareMedications} onChange={e => update("aftercareMedications", e.target.value)}
                placeholder="Current medications, dosages, prescribing provider, refill instructions..." />
            </div>

            <div style={S.fieldGroup}>
              <label style={S.label}>Additional Notes</label>
              <textarea style={{ ...S.textarea, minHeight: 60 }} value={form.finalNotes} onChange={e => update("finalNotes", e.target.value)}
                placeholder="Any other relevant information..." />
            </div>
          </div>
        )}

        {step === 1 && (
          <div>
            <div style={{ fontSize: 13, fontWeight: 800, color: "#fc8181", marginBottom: 12, display: "flex", alignItems: "center", gap: 8 }}>
              <ClipboardList size={16} /> Review Discharge
            </div>

            <div style={S.card}>
              <div style={{ fontSize: 11, fontWeight: 800, color: "#f6ad55", textTransform: "uppercase", letterSpacing: 1, marginBottom: 8 }}>Individual</div>
              <div style={{ fontSize: 16, fontWeight: 800 }}>{ind.firstName} {ind.lastName}</div>
              {ind.dob && <div style={{ fontSize: 12, color: "#a0aec0" }}>DOB: {ind.dob}</div>}
              {ind.program && <div style={{ fontSize: 12, color: "#a0aec0" }}>Program: {ind.program}</div>}
              {ind.admissionDate && <div style={{ fontSize: 12, color: "#a0aec0" }}>Admitted: {new Date(ind.admissionDate).toLocaleDateString()}</div>}
            </div>

            <div style={S.card}>
              <div style={{ fontSize: 11, fontWeight: 800, color: "#f6ad55", textTransform: "uppercase", letterSpacing: 1, marginBottom: 8 }}>Reason</div>
              <div style={{ fontSize: 13, color: "#e2e8f0" }}>{reasonLabel}</div>
            </div>

            <div style={S.card}>
              <div style={{ fontSize: 11, fontWeight: 800, color: "#f6ad55", textTransform: "uppercase", letterSpacing: 1, marginBottom: 8 }}>Summary</div>
              <div style={{ fontSize: 12, color: "#a0aec0", lineHeight: 1.6, whiteSpace: "pre-wrap" }}>{form.summary || "—"}</div>
            </div>

            {(form.aftercarePlan || form.aftercareProviders || form.aftercareMedications) && (
              <div style={S.card}>
                <div style={{ fontSize: 11, fontWeight: 800, color: "#f6ad55", textTransform: "uppercase", letterSpacing: 1, marginBottom: 8 }}>Aftercare Plan</div>
                {form.aftercarePlan && <div style={{ fontSize: 12, color: "#a0aec0", lineHeight: 1.6, marginBottom: 8, whiteSpace: "pre-wrap" }}>{form.aftercarePlan}</div>}
                {form.aftercareProviders && (
                  <div style={{ marginBottom: 8 }}>
                    <div style={{ fontSize: 10, fontWeight: 700, color: "#4fd1c5", marginBottom: 2 }}>Referral Providers</div>
                    <div style={{ fontSize: 12, color: "#a0aec0", whiteSpace: "pre-wrap" }}>{form.aftercareProviders}</div>
                  </div>
                )}
                {form.aftercareMedications && (
                  <div>
                    <div style={{ fontSize: 10, fontWeight: 700, color: "#4fd1c5", marginBottom: 2 }}>Medications</div>
                    <div style={{ fontSize: 12, color: "#a0aec0", whiteSpace: "pre-wrap" }}>{form.aftercareMedications}</div>
                  </div>
                )}
              </div>
            )}

            {form.finalNotes && (
              <div style={S.card}>
                <div style={{ fontSize: 11, fontWeight: 800, color: "#f6ad55", textTransform: "uppercase", letterSpacing: 1, marginBottom: 8 }}>Additional Notes</div>
                <div style={{ fontSize: 12, color: "#a0aec0", lineHeight: 1.6, whiteSpace: "pre-wrap" }}>{form.finalNotes}</div>
              </div>
            )}

            {/* Confirmation Checkbox */}
            <div style={{
              ...S.card, borderColor: form.confirmDischarge ? "rgba(104,211,145,0.2)" : "rgba(252,129,129,0.15)",
              background: form.confirmDischarge ? "rgba(104,211,145,0.04)" : "rgba(252,129,129,0.04)",
            }}>
              <label style={{ display: "flex", gap: 12, cursor: "pointer", alignItems: "flex-start" }}>
                <input type="checkbox" checked={form.confirmDischarge} onChange={e => update("confirmDischarge", e.target.checked)}
                  style={{ marginTop: 3, flexShrink: 0 }} />
                <div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: form.confirmDischarge ? "#68d391" : "#fc8181" }}>
                    I confirm this discharge
                  </div>
                  <div style={{ fontSize: 11, color: "#718096", lineHeight: 1.5, marginTop: 4 }}>
                    I have reviewed the discharge information and confirm that {ind.firstName} {ind.lastName} is being discharged
                    from services. This action will be recorded in the audit trail with my name and timestamp.
                  </div>
                </div>
              </label>
            </div>
          </div>
        )}

        {/* Error */}
        {error && (
          <div style={{
            ...S.card, borderColor: "rgba(252,129,129,0.2)", background: "rgba(252,129,129,0.06)",
            display: "flex", alignItems: "center", gap: 8,
          }}>
            <AlertTriangle size={14} style={{ color: "#fc8181", flexShrink: 0 }} />
            <span style={{ fontSize: 13, color: "#fc8181", fontWeight: 600 }}>{error}</span>
          </div>
        )}
      </div>

      {/* Footer Nav */}
      <div style={{
        position: "fixed", bottom: 0, left: 0, right: 0,
        padding: "12px 20px 24px", background: "linear-gradient(0deg, #0a0f1a 60%, transparent)", zIndex: 40,
      }}>
        <div style={{ display: "flex", gap: 10, maxWidth: 512, margin: "0 auto" }}>
          {step === 1 && (
            <button onClick={() => { setStep(0); setError(""); }} style={{
              flex: 1, padding: "14px 0", borderRadius: 14, fontSize: 14, fontWeight: 700,
              background: "rgba(99,179,237,0.08)", color: "#63b3ed",
              border: "1px solid rgba(99,179,237,0.15)", cursor: "pointer",
              display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
            }}>
              <ArrowLeft size={16} /> Edit
            </button>
          )}

          {step === 0 ? (
            <button onClick={() => {
              if (!form.reason) { setError("Discharge reason is required."); return; }
              if (!form.summary.trim()) { setError("Discharge summary is required."); return; }
              setError("");
              setStep(1);
            }} style={{
              flex: 2, padding: "14px 0", borderRadius: 14, fontSize: 14, fontWeight: 800,
              background: "linear-gradient(135deg, #fc8181, #e53e3e)",
              color: "white", border: "none", cursor: "pointer",
              display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
            }}>
              Review Discharge <ArrowRight size={16} />
            </button>
          ) : (
            <button onClick={handleSubmit} disabled={saving || !form.confirmDischarge} style={{
              flex: 2, padding: "14px 0", borderRadius: 14, fontSize: 14, fontWeight: 800,
              background: saving || !form.confirmDischarge ? "rgba(252,129,129,0.2)" : "linear-gradient(135deg, #fc8181, #e53e3e)",
              color: "white", border: "none", cursor: saving || !form.confirmDischarge ? "default" : "pointer",
              display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
            }}>
              {saving ? "Discharging..." : <><LogOut size={16} /> Confirm Discharge</>}
            </button>
          )}
        </div>
      </div>

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}